import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useCallback, useReducer, useEffect, useState, useMemo, useRef, } from "react";
import { createPublicClient, http } from "viem";
import { Account, getExternalPlatformIdFromInternalPlatformId, PlatformEnvironment, validateAndFormatAddress, VMType, } from "@orb-labs/types";
import { orbyActions } from "@orb-labs/orby-viem-extension";
import { OAuthPermissionLevel, OrbyLinkOperation, } from "../../utils/types.js";
import { storeEncryptedTokens, getEncryptedTokens, removeEncryptedTokens } from "../../utils/oauth.js";
import { createOrbyLink } from "../../components/OrbyProvider/OrbyLink.js";
export var Action;
(function (Action) {
    Action["UPDATE_BASE_CLIENT"] = "update_base_client";
    Action["UPDATE_ACCOUNT_CLUSTER"] = "update_account_cluster";
    Action["UPDATE_PLATFORMS"] = "update_platforms";
    Action["SET_VIRTUAL_NODE"] = "set_virtual_node";
    Action["BULK_SET_VIRTUAL_NODES"] = "bulk_set_virtual_node";
    Action["UPDATE_VIRTUAL_NODE_URLS"] = "update_virtual_node_urls";
})(Action || (Action = {}));
export const orbyContextReducer = (state, data) => {
    switch (data.action) {
        case Action.UPDATE_ACCOUNT_CLUSTER:
            const { accountCluster } = data.payload;
            return { ...state, accountCluster };
        case Action.UPDATE_BASE_CLIENT:
            const { virtualNode: baseClient, environment } = data.payload;
            if (environment === "mainnet") {
                return { ...state, baseMainnetClient: baseClient };
            }
            return { ...state, baseTestnetClient: baseClient };
        case Action.SET_VIRTUAL_NODE:
            const updateOperationStatePayload = data.payload;
            const { platformId, address, virtualNode } = updateOperationStatePayload;
            const key = `${address}-${platformId.toString()}`;
            state.virtualNodes.set(key, virtualNode);
            return { ...state };
        case Action.BULK_SET_VIRTUAL_NODES:
            const { virtualNodes } = data.payload;
            virtualNodes?.map(({ platformId, address, virtualNode }) => {
                const key = `${address}-${platformId.toString()}`;
                state.virtualNodes.set(key, virtualNode);
            });
            return { ...state };
        case Action.UPDATE_PLATFORMS:
            const { supportedPlatforms } = data.payload;
            return { ...state, supportedPlatforms };
        case Action.UPDATE_VIRTUAL_NODE_URLS:
            const { virtualNodeRpcUrlsForSupportedPlatforms, clearPrevious } = data.payload;
            const rpcsState = clearPrevious
                ? new Map()
                : state.virtualNodeRpcUrlsForSupportedPlatforms;
            virtualNodeRpcUrlsForSupportedPlatforms.map((info) => {
                const key = `${info.entrypointAccountAddress}-${info.platformId.toString()}`;
                rpcsState.set(key, info);
            });
            return { ...state, virtualNodeRpcUrlsForSupportedPlatforms: rpcsState };
        default:
            return state;
    }
};
// Context creation
const OrbyContext = createContext(undefined);
export const useOrby = () => {
    const context = useContext(OrbyContext);
    if (context == undefined) {
        throw new Error("useOrby should be used in the OrbyProvider");
    }
    return context;
};
export function OrbyProvider({ config, children }) {
    const [virtualNodeLoaded, setVirtualNodeLoaded] = useState(false);
    const baseUrl = config.baseUrl ?? "https://api-rpc-dev.orblabs.xyz";
    const baseMainnetClient = useMemo(() => createPublicClient({
        transport: http(`${baseUrl}/${config.instancePrivateAPIKey}/mainnet`),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).extend(orbyActions), [baseUrl, config.instancePrivateAPIKey]);
    const baseTestnetClient = useMemo(() => createPublicClient({
        transport: http(`${baseUrl}/${config.instancePrivateAPIKey}/testnet`),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).extend(orbyActions), [baseUrl, config.instancePrivateAPIKey]);
    const initialState = {
        virtualNodes: new Map(),
        baseMainnetClient,
        baseTestnetClient,
        supportedPlatforms: [],
        virtualNodeRpcUrlsForSupportedPlatforms: new Map(),
    };
    const [state, dispatch] = useReducer(orbyContextReducer, initialState);
    const cacheVirtualNode = useCallback((address, platformId, virtualNode) => {
        dispatch({
            action: Action.SET_VIRTUAL_NODE,
            payload: { platformId, address, virtualNode },
        });
    }, []);
    // Track if platforms have been fetched to prevent duplicate calls
    const platformsFetchedRef = useRef(false);
    const fetchPlatforms = useCallback(async () => {
        // Skip if we already fetched platforms AND state has been updated
        if (platformsFetchedRef.current && state.supportedPlatforms.length > 0) {
            return state.supportedPlatforms;
        }
        if (!baseMainnetClient || !baseTestnetClient) {
            return;
        }
        const [mainnets, testnets] = await Promise.all([
            baseMainnetClient.listPlatformsInformation(),
            baseTestnetClient.listPlatformsInformation(),
        ]);
        if (!mainnets?.platforms || !testnets?.platforms) {
            return;
        }
        const supportedPlatforms = mainnets.platforms.concat(testnets.platforms);
        platformsFetchedRef.current = true;
        dispatch({
            action: Action.UPDATE_PLATFORMS,
            payload: { supportedPlatforms },
        });
        return supportedPlatforms;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseMainnetClient, baseTestnetClient]);
    // Track current accountClusterId to prevent duplicate updates
    const currentAccountClusterIdRef = useRef(null);
    const updateAccountCluster = useCallback(async (accountCluster) => {
        // Skip if this is the same account cluster we already processed,
        // unless the user was previously missing and is now available (auth retry)
        const isSameCluster = currentAccountClusterIdRef.current === accountCluster?.accountClusterId;
        if (isSameCluster && accountCluster?.user) {
            // Same cluster but user is now available - update the state without re-fetching virtual nodes
            dispatch({
                action: Action.UPDATE_ACCOUNT_CLUSTER,
                payload: { accountCluster },
            });
            isCreatingClusterRef.current = false;
            return;
        }
        else if (isSameCluster) {
            return;
        }
        setVirtualNodeLoaded(false);
        try {
            if (accountCluster) {
                currentAccountClusterIdRef.current = accountCluster.accountClusterId;
                dispatch({
                    action: Action.UPDATE_ACCOUNT_CLUSTER,
                    payload: { accountCluster },
                });
                const virtualNodeRpcUrlsForSupportedPlatforms = await baseMainnetClient?.getVirtualNodeRpcUrlsForSupportedPlatforms(accountCluster?.accountClusterId);
                if (virtualNodeRpcUrlsForSupportedPlatforms) {
                    dispatch({
                        action: Action.UPDATE_VIRTUAL_NODE_URLS,
                        payload: {
                            virtualNodeRpcUrlsForSupportedPlatforms,
                            clearPrevious: true,
                        },
                    });
                    const virtualNodes = virtualNodeRpcUrlsForSupportedPlatforms?.map(({ platformId, entrypointAccountAddress, virtualNodeRpcUrl }) => {
                        const virtualNode = createPublicClient({
                            transport: http(virtualNodeRpcUrl),
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        }).extend(orbyActions);
                        return {
                            platformId,
                            address: entrypointAccountAddress,
                            virtualNode,
                        };
                    });
                    dispatch({
                        action: Action.BULK_SET_VIRTUAL_NODES,
                        payload: { virtualNodes },
                    });
                    setVirtualNodeLoaded(true);
                }
            }
        }
        catch (error) {
            console.error("[updateAccountCluster]", error);
        }
        finally {
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.accounts, baseMainnetClient]);
    // Track if we're already creating an account cluster to prevent duplicate calls
    const isCreatingClusterRef = useRef(false);
    // Track whether the cluster was created without authentication so we can re-create when auth arrives
    const clusterCreatedWithoutAuthRef = useRef(false);
    const createAccountCluster = useCallback(async () => {
        try {
            const accounts = config?.accounts?.filter((account) => account?.address && validateAndFormatAddress(account?.address) != "") ||
                [];
            const supportedPlatforms = await fetchPlatforms();
            if (!supportedPlatforms || supportedPlatforms.length === 0) {
                return;
            }
            const promises = supportedPlatforms.map(async (platform) => {
                const savedWalletId = await getEncryptedTokens(platform.platformId);
                if (savedWalletId) {
                    const parsedWalletId = JSON.parse(savedWalletId);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const cexAccounts = parsedWalletId.accounts.map((account) => Account.toAccount(account));
                    return cexAccounts;
                }
            });
            const savedCexAccounts = await Promise.all(promises).then((results) => results.filter((result) => result !== undefined).flat());
            const allAccounts = [...accounts, ...savedCexAccounts];
            if (allAccounts.length === 0) {
                return;
            }
            // Skip if we're already in the middle of creating a cluster
            if (isCreatingClusterRef.current) {
                return;
            }
            // Allow re-creation if authentication arrived after the cluster was initially created without it.
            // The backend's idempotency collision path will attach the user to the existing cluster.
            const isAuthRetry = clusterCreatedWithoutAuthRef.current && config.authentication;
            // Recreate cluster when the current one does not include all configured accounts
            // (e.g. a newly linked Mesh account was added after initial cluster creation).
            const existingClusterAccounts = state.accountCluster?.accounts ?? [];
            const hasMissingAccounts = allAccounts.some((candidate) => {
                const candidateAddress = candidate.address;
                const candidatePlatformId = candidate.platformId?.toString();
                return !existingClusterAccounts.some((existing) => {
                    const existingAddress = existing.address;
                    const existingPlatformId = existing.platformId?.toString();
                    return existingAddress == candidateAddress && existingPlatformId == candidatePlatformId;
                });
            });
            // Skip only when we already have a valid cluster and no meaningful account/auth change happened.
            if (currentAccountClusterIdRef.current && !isAuthRetry && !hasMissingAccounts) {
                return;
            }
            isCreatingClusterRef.current = true;
            // Avoid toggling global loading when we are only refreshing an existing cluster.
            if (!state.accountCluster?.accountClusterId) {
                setVirtualNodeLoaded(false);
            }
            const accountCluster = await baseMainnetClient?.createAccountCluster(allAccounts, config.authentication);
            // Track whether we created with or without authentication
            clusterCreatedWithoutAuthRef.current = !config.authentication;
            updateAccountCluster(accountCluster);
        }
        catch (error) {
            console.error("[createAccountCluster]", error);
            isCreatingClusterRef.current = false; // Reset on error so we can retry
        }
        finally {
            // Allow future re-creation attempts when accounts/auth state changes.
            isCreatingClusterRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        config?.accounts,
        config?.authentication,
        baseMainnetClient,
        updateAccountCluster,
        state.accountCluster?.accounts,
    ]);
    useEffect(() => {
        // Only call createAccountCluster - it calls fetchPlatforms internally
        if (config.accounts?.length > 0 && baseMainnetClient) {
            createAccountCluster();
        }
        else {
            // Only fetch platforms if we don't have accounts (no cluster to create)
            fetchPlatforms();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.accounts, config.authentication, baseMainnetClient]);
    const getCachedVirtualNode = useCallback((address, platformId) => {
        if (!address || !platformId) {
            return;
        }
        const virtualNodeKey = `${validateAndFormatAddress(address)}-${platformId.toString()}`;
        return state.virtualNodes.get(virtualNodeKey);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.virtualNodes]);
    const orbyConnection = useMemo(() => createOrbyLink({
        baseUrl: "http://localhost:4001", // Your OAuth backend URL
        onIntegrationConnected: async (data) => {
            updateAccountCluster(data.accountCluster);
            const { tokens } = data;
            if ([OAuthPermissionLevel.WRITE, OAuthPermissionLevel.READ_AND_WRITE].includes(tokens.permissions)) {
                await storeEncryptedTokens(tokens.platformId, JSON.stringify(tokens));
            }
        },
        onAccountLinked: (data) => {
            updateAccountCluster(data.accountCluster);
        },
        onExit: (error) => {
            if (error) {
                // handle error
            }
            else {
                // ...
            }
        },
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseUrl]);
    const getOrRefreshSavedTokens = useCallback(async (platformId) => {
        const baseUrl = config.baseUrl ?? "http://localhost:4001";
        const storedTokens = await getEncryptedTokens(platformId);
        if (!storedTokens) {
            return null;
        }
        let tokens;
        try {
            tokens = JSON.parse(storedTokens);
        }
        catch (error) {
            console.error(`[refreshTokens] Failed to parse tokens for platform ${platformId.toString()}:`, error);
            return null;
        }
        // Check if tokens are expired or about to expire (within 5 minutes)
        const now = new Date();
        const expiresAt = tokens.expiresAt ? new Date(tokens.expiresAt) : null;
        const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
        const isExpired = expiresAt && now.getTime() >= expiresAt.getTime() - bufferTime;
        // If tokens are still valid, return them
        if (!isExpired) {
            return tokens;
        }
        // If no refresh token, cannot refresh
        if (!tokens.refreshToken) {
            console.warn(`[refreshTokens] No refresh token available for platform ${platformId.toString()}`);
            return null;
        }
        // Check if refresh token itself is expired
        if (tokens.refreshTokenExpiresAt && now.getTime() >= tokens.refreshTokenExpiresAt) {
            console.warn(`[refreshTokens] Refresh token expired for platform ${platformId.toString()}`);
            // Remove expired tokens
            await removeEncryptedTokens(platformId);
            return null;
        }
        try {
            // Call refresh token endpoint
            // TODO(felix): find a way to make this url dynamic
            const refreshUrl = `${baseUrl.replace(/\/$/, "")}/refresh-token`;
            const response = await fetch(refreshUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    refreshToken: tokens.refreshToken,
                    platformId: platformId.toString(),
                }),
            });
            if (!response.ok) {
                return undefined;
            }
            const refreshedData = await response.json();
            // TODO(felix): find a way to get the refreshed tokens from the response
            const refreshedTokens = {
                accessToken: refreshedData.accessToken,
                refreshToken: refreshedData.refreshToken || tokens.refreshToken,
                expiresAt: refreshedData.expiresAt ? new Date(refreshedData.expiresAt) : undefined,
                refreshTokenExpiresAt: refreshedData.refreshTokenExpiresAt,
                platformId: tokens.platformId,
                permissions: tokens.permissions,
            };
            await storeEncryptedTokens(platformId, JSON.stringify(refreshedTokens));
            return refreshedTokens;
        }
        catch (error) {
            console.error(`[refreshTokens] Failed to refresh tokens for platform ${platformId.toString()}:`, error);
            // If refresh fails, remove invalid tokens
            await removeEncryptedTokens(platformId);
            return null;
        }
    }, [config.baseUrl]);
    const prepareOperationExecutionOnLinkedAccount = useCallback(async (operations) => {
        const tokenMap = new Map();
        // Get unique combinations of from+platformId from operations
        const uniqueKeys = new Set();
        const keyToPlatformId = new Map();
        for (const op of operations) {
            const from = op.from || "";
            // Use "|" as delimiter since addresses are hex and won't contain this character
            const key = `${from}|${op.platformId.toString()}`;
            uniqueKeys.add(key);
            keyToPlatformId.set(key, op.platformId);
        }
        // Process each unique from+platformId combination
        for (const key of uniqueKeys) {
            // Get platformId from the map we created
            const platformId = keyToPlatformId.get(key);
            if (!platformId) {
                continue;
            }
            const platform = state.supportedPlatforms?.find((p) => p.platformId === platformId);
            if (platform?.vmType !== VMType.OFFCHAIN) {
                continue;
            }
            let writeTokens = await getOrRefreshSavedTokens(platformId);
            if (!writeTokens) {
                // Convert platformId to orbyPlatformId based on vmType
                const orbyPlatformId = getExternalPlatformIdFromInternalPlatformId(platformId);
                // Wait for tokens to be stored before continuing to next platform
                await new Promise((resolve, reject) => {
                    let timeoutId;
                    let checkIntervalId;
                    const cleanup = () => {
                        if (timeoutId)
                            clearTimeout(timeoutId);
                        if (checkIntervalId)
                            clearInterval(checkIntervalId);
                    };
                    // Set up polling to check for tokens
                    // Note: We use polling instead of storage events because storage events
                    // only fire in OTHER windows/tabs, not in the same window where the change occurs
                    checkIntervalId = setInterval(async () => {
                        const tokens = await getEncryptedTokens(platformId);
                        if (tokens) {
                            cleanup();
                            resolve();
                        }
                    }, 100);
                    // Set a timeout to reject if tokens aren't obtained within a reasonable time
                    timeoutId = setTimeout(() => {
                        cleanup();
                        reject(new Error(`Timeout waiting for write tokens for platform ${platformId.toString()}`));
                    }, 5 * 60 * 1000); // 5 minutes timeout
                    // Open the OAuth link
                    orbyConnection.openOrbyLink(OAuthPermissionLevel.READ_AND_WRITE, OrbyLinkOperation.PAY_WITH_CRYPTO, state.accountCluster?.accountClusterId, undefined, undefined, orbyPlatformId);
                });
                // After OAuth completes, get the tokens
                writeTokens = await getOrRefreshSavedTokens(platformId);
            }
            // Store tokens in map if we have them
            if (writeTokens) {
                tokenMap.set(key, writeTokens);
            }
        }
        return tokenMap;
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.supportedPlatforms, state.accountCluster, orbyConnection, baseMainnetClient, getOrRefreshSavedTokens]);
    const executeOperationOnLinkedAccount = useCallback(async (operation) => {
        try {
            await prepareOperationExecutionOnLinkedAccount([operation]);
            const writeTokens = await getOrRefreshSavedTokens(operation.platformId);
            if (!writeTokens) {
                return undefined;
            }
            const response = await fetch(operation.txRpcUrl, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${writeTokens.accessToken}`,
                    "Content-Type": "application/json",
                },
                body: operation.data,
            });
            if (!response.ok) {
                return undefined;
            }
            const txData = await response.json();
            // TODO(felix): find a way to get the tx hash from the response
            return txData.id;
        }
        catch (error) {
            console.error("[executeWithdrawal] Failed to execute withdrawal:", error);
            return undefined;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const connectToAccount = useCallback(async () => {
        try {
            orbyConnection.linkAccount(state.accountCluster?.accountClusterId, undefined, undefined);
        }
        catch (error) {
            console.error("[] connectToAccount error", error);
            throw error;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.accountCluster]);
    const _deposit = useCallback(async () => {
        try {
            orbyConnection.deposit({
                tokenAddress: "0x0000000000000000000000000000000000000000",
                tokenPlatformId: BigInt(1),
                tokenAmount: BigInt(1),
                paymentAddress: "0x0000000000000000000000000000000000000000",
            }, undefined, undefined, undefined);
        }
        catch (error) {
            console.error("[] deposit error", error);
            throw error;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.accountCluster]);
    const getRandomVirtualNode = useCallback((platformId) => {
        const virtualNodeEntries = Array.from(state.virtualNodes.entries());
        if (virtualNodeEntries.length === 0) {
            return undefined;
        }
        // If platformId is specified, filter virtual nodes by platformId
        let filteredEntries = virtualNodeEntries;
        if (platformId) {
            filteredEntries = virtualNodeEntries.filter(([key]) => {
                // Extract platformId from the key format: "${address}-${platformId}"
                const parts = key.split("-");
                const nodePlatformId = parts[parts.length - 1]; // Last part should be platformId
                return BigInt(nodePlatformId) === platformId;
            });
        }
        if (filteredEntries.length === 0) {
            return undefined;
        }
        // Return a random virtual node
        const randomIndex = Math.floor(Math.random() * filteredEntries.length);
        return filteredEntries[randomIndex][1]; // Return the virtual node (value)
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.virtualNodes]);
    const getVirtualNodeRpcUrlsForSupportedPlatforms = useCallback(() => Array.from(state.virtualNodeRpcUrlsForSupportedPlatforms.values()), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.virtualNodeRpcUrlsForSupportedPlatforms]);
    const getVirtualNodeRpcUrl = useCallback((address, platformId) => {
        if (!address || !platformId) {
            return;
        }
        const virtualNodeKey = `${validateAndFormatAddress(address)}-${platformId.toString()}`;
        return state.virtualNodeRpcUrlsForSupportedPlatforms.get(virtualNodeKey);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.virtualNodeRpcUrlsForSupportedPlatforms]);
    const getBaseClient = useCallback((platformId) => {
        if (!platformId) {
            console.error("[getBaseClient] platformId is not set");
            return;
        }
        const platformInfo = state?.supportedPlatforms?.find((platformInfo) => platformInfo?.platformId == platformId);
        if (!platformInfo) {
            console.error("[getBaseClient] platformInfo is not set");
            return undefined;
        }
        else if (platformInfo?.environment === PlatformEnvironment.MAINNET) {
            return baseMainnetClient;
        }
        return baseTestnetClient;
    }, [state.supportedPlatforms, baseMainnetClient, baseTestnetClient]);
    const getPlatformFromPlatformId = useCallback((platformId) => state?.supportedPlatforms?.find((platformInfo) => platformInfo?.platformId == platformId), [state.supportedPlatforms]);
    const contextValue = useMemo(() => ({
        config,
        accountCluster: state.accountCluster,
        baseMainnetClient: state.baseMainnetClient,
        baseTestnetClient: state.baseTestnetClient,
        orbyConnection,
        cacheVirtualNode,
        getCachedVirtualNode,
        getRandomVirtualNode,
        executeOperationOnLinkedAccount,
        loading: !virtualNodeLoaded,
        supportedPlatforms: state?.supportedPlatforms,
        getVirtualNodeRpcUrlsForSupportedPlatforms,
        getVirtualNodeRpcUrl,
        updateAccountCluster,
        getBaseClient,
        connectToAccount,
        getWithdrawalQuote: undefined,
        executeWithdrawalFromCentralizedExchange: undefined,
        getPlatformFromPlatformId,
    }), 
    // Only include data values that should trigger consumer re-renders
    // Callback functions are stable via useCallback and don't need to trigger re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        config,
        state.accountCluster,
        state.baseMainnetClient,
        state.baseTestnetClient,
        state.supportedPlatforms,
        virtualNodeLoaded,
    ]);
    return _jsx(OrbyContext.Provider, { value: contextValue, children: children });
}
